import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Field, EmptyState } from "@/components/common";
import { SellBlocked } from "@/components/TrialBanner";
import { api, brl, errMsg, fmtDateTime, todayISO } from "@/lib/api";
import { HistoryFilters } from "@/components/comanda/HistoryFilters";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const TABS = [["open", "Abertas"], ["closed", "Fechadas"], ["cancelled", "Canceladas"]];
const EMPTY = { number: "", customer_name: "", table_id: "none", people: "" };

function NewComandaForm({ form, setForm, tables }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Número da comanda *"><Input type="number" min="1" value={form.number} onChange={set("number")} className="font-mono" autoFocus data-testid="new-comanda-number-input" /></Field>
      <Field label="Nome do cliente (opcional)"><Input value={form.customer_name} onChange={set("customer_name")} data-testid="new-comanda-name-input" /></Field>
      <Field label="Mesa (opcional)">
        <Select value={form.table_id} onValueChange={(v) => setForm((f) => ({ ...f, table_id: v }))}>
          <SelectTrigger data-testid="new-comanda-table-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Sem mesa (avulsa)</SelectItem>
            {tables.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`new-comanda-table-${t.number}`}>Mesa {t.number}{t.name ? ` · ${t.name}` : ""}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Quantidade de pessoas (opcional)"><Input type="number" min="1" value={form.people} onChange={set("people")} data-testid="new-comanda-people-input" /></Field>
    </div>
  );
}

export default function Comandas() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState("open");
  const [list, setList] = useState([]);
  const [f, setF] = useState({ start: todayISO(), end: todayISO(), by: "closed", user_name: "" });
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(params.get("nova") === "1");
  const [form, setForm] = useState(EMPTY);
  const [tables, setTables] = useState([]);
  const navigate = useNavigate();
  const { session } = useAuth();
  const waiter = !!session.user.waiter_view;

  useEffect(() => {
    if (status === "open") { api.get("/comandas?status=open").then((r) => setList(r.data)); return; }
    if (!f.start || !f.end) return;
    api.get("/comandas/history", { params: { status, ...f } }).then((r) => { setList(r.data.rows); setUsers(r.data.users); }).catch((e) => toast.error(errMsg(e)));
  }, [status, f]);
  const done = status !== "open";
  useEffect(() => {
    if (creating) { setForm(EMPTY); api.get("/tables").then((r) => setTables(r.data.filter((t) => !t.comanda_id))).catch(() => setTables([])); }
  }, [creating]);

  const create = async () => {
    try {
      const { data } = await api.post("/comandas", { number: Number(form.number), customer_name: form.customer_name.trim(), table_id: form.table_id === "none" ? null : form.table_id, people: form.people ? Number(form.people) : null });
      navigate(`/app/comandas/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const close = (o) => {
    setCreating(o);
    if (!o && params.get("nova")) setParams({});
  };

  return (
    <>
      <PageHeader eyebrow="Consumo" title="Comandas" actions={<Button onClick={() => setCreating(true)} data-testid="new-comanda-btn"><Plus className="h-4 w-4 mr-1" />Nova comanda</Button>} />
      <SellBlocked />
      <div className="flex gap-2">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setStatus(k)} data-testid={`comandas-tab-${k}`}
            className={cn("rounded-full border px-4 py-2 text-sm font-medium touch", status === k ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent")}>{l}</button>
        ))}
      </div>
      {done && <HistoryFilters f={f} setF={setF} users={users} status={status} />}
      {!list.length ? <EmptyState icon={ClipboardList} title="Nenhuma comanda" text={done ? "Nenhuma comanda neste período. Mude as datas ou o usuário." : "Abra uma comanda avulsa ou a partir de uma mesa."} /> : (
        <div className="card-surface overflow-x-auto">
          <Table data-testid="comandas-list-table">
            <TableHeader><TableRow><TableHead>Nº</TableHead><TableHead>Cliente</TableHead><TableHead>Mesa</TableHead><TableHead>Pessoas</TableHead><TableHead>Itens</TableHead>{waiter && <TableHead>Garçom</TableHead>}<TableHead>Aberta em</TableHead>{done && <><TableHead>{status === "cancelled" ? "Cancelada em" : "Fechada em"}</TableHead><TableHead>{status === "cancelled" ? "Cancelada por" : "Fechada por"}</TableHead></>}<TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>
              {list.map((c) => (
                <TableRow key={c.id} className="cursor-pointer" onClick={() => navigate(`/app/comandas/${c.id}`)} data-testid={`comanda-row-${c.number}`}>
                  <TableCell className="font-mono font-bold">#{c.number}</TableCell>
                  <TableCell>{c.customer_name || "—"}</TableCell>
                  <TableCell>{c.table_number ? `Mesa ${c.table_number}` : "Avulsa"}</TableCell>
                  <TableCell>{c.people || "—"}</TableCell>
                  <TableCell>{c.items.length}</TableCell>
                  {waiter && <TableCell data-testid={`comanda-waiter-${c.number}`}>{c.opened_by || "—"}</TableCell>}
                  <TableCell className="text-muted-foreground">{fmtDateTime(c.opened_at)}</TableCell>
                  {done && <><TableCell className="text-muted-foreground">{fmtDateTime(c.closed_at)}</TableCell><TableCell data-testid={`comanda-closer-${c.number}`}>{c.closed_by || "—"}</TableCell></>}
                  <TableCell className="text-right font-mono font-semibold">{brl(c.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={creating} onOpenChange={close}>
        <DialogContent data-testid="new-comanda-modal">
          <DialogHeader><DialogTitle>Nova comanda</DialogTitle></DialogHeader>
          <NewComandaForm form={form} setForm={setForm} tables={tables} />
          <p className="text-xs text-muted-foreground">O número não pode repetir enquanto houver outra comanda aberta com ele. Também é possível abrir pela tela de <Link to="/app/mesas" className="text-primary">Mesas</Link>.</p>
          <DialogFooter><Button onClick={create} disabled={!(Number(form.number) >= 1)} data-testid="confirm-new-comanda-btn">Abrir comanda</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
